import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  MicrophoneMutedIcon,
  MicrophoneOn,
  MicrophoneOn1,
  MicrophoneOn2,
  MicrophoneOn3,
  MicrophoneOn4,
} from '../../../images'
import { useIsTrackEnabled } from './use_is_track_enabled'

const AudioContext = window.AudioContext || window.webkitAudioContext
const audioContext = AudioContext ? new AudioContext() : null
const OFF = 'OFF'

function rootMeanSquare(samples) {
  const sumSq = samples.reduce((sumSq, sample) => sumSq + sample * sample, 0)
  return Math.sqrt(sumSq / samples.length)
}

export async function pollAudioLevel(
  mediaStreamTrack,
  requestAnimateRef,
  onLevelChanged,
  levelRef,
  maxLevel = 10,
) {
  if (!audioContext) {
    return
  }

  // Due to browsers' autoplay policy, the AudioContext is only active after
  // the user has interacted with your app, after which the Promise returned
  // here is resolved.
  await audioContext.resume()

  // Create an analyser to access the raw audio samples from the microphone.
  const analyser = audioContext.createAnalyser()
  analyser.fftSize = 1024
  analyser.smoothingTimeConstant = 0.5

  // Connect the LocalAudioTrack's media source to the analyser.
  const stream = new MediaStream([mediaStreamTrack])
  const source = audioContext.createMediaStreamSource(stream)
  source.connect(analyser)

  const samples = new Uint8Array(analyser.frequencyBinCount)

  // Periodically calculate the audio level from the captured samples,
  // and if changed, call the callback with the new audio level.
  requestAnimateRef.current = requestAnimationFrame(function checkLevel() {
    const { readyState, muted } = mediaStreamTrack
    if (readyState === 'live' && !muted) {
      analyser.getByteFrequencyData(samples)
      const rms = rootMeanSquare(samples)
      const log2Rms = rms && Math.log2(rms)

      // Audio level ranges from 0 (silence) to 10 (loudest).
      const newLevel = Math.ceil((maxLevel * log2Rms) / 8)
      if (newLevel !== levelRef?.current) {
        onLevelChanged(newLevel)
      }
      requestAnimateRef.current = requestAnimationFrame(checkLevel)
    }
  })
}

export function useAudioIndicator(audioTrack) {
  const isEnabled = useIsTrackEnabled(audioTrack)
  const levelRef = useRef(0)
  const [icon, setIcon] = useState(<MicrophoneOn title={'level-0'} />)
  const requestAnimateRef = useRef()

  const handleLevelChanged = useCallback((mediaStreamLevel) => {
    switch (true) {
      case mediaStreamLevel > 5 && mediaStreamLevel < 7:
        if (levelRef.current !== 1) {
          levelRef.current = 1
          setIcon(<MicrophoneOn1 title={'level-1'} />)
        }
        break
      case mediaStreamLevel >= 7 && mediaStreamLevel < 8:
        if (levelRef.current !== 2) {
          levelRef.current = 2
          setIcon(<MicrophoneOn2 title={'level-2'} />)
        }
        break
      case mediaStreamLevel >= 8 && mediaStreamLevel < 9:
        if (levelRef.current !== 3) {
          levelRef.current = 3
          setIcon(<MicrophoneOn3 title={'level-3'} />)
        }
        break
      case mediaStreamLevel >= 9:
        if (levelRef.current !== 4) {
          levelRef.current = 4
          setIcon(<MicrophoneOn4 title={'level-4'} />)
        }
        break
      default:
        if (levelRef.current !== 0) {
          levelRef.current = 0
          setIcon(<MicrophoneOn title={'level-0'} />)
        }
    }
  }, [])

  useEffect(() => {
    if (audioTrack && isEnabled) {
      levelRef.current = 0
      setIcon(<MicrophoneOn title={'level-0'} />)
      pollAudioLevel(
        audioTrack.mediaStreamTrack,
        requestAnimateRef,
        handleLevelChanged,
        levelRef,
      )
    } else {
      levelRef.current = OFF
      setIcon(<MicrophoneMutedIcon title={'muted'} />)
    }

    return () => {
      cancelAnimationFrame(requestAnimateRef.current)
    }
  }, [audioTrack, handleLevelChanged, isEnabled])

  return icon
}
